import { Component, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { UsersList } from '../../components/users-list/users-list';
import { UserFilter, UserFilterModel } from '../../components/user-filter/user-filter';
import { Router } from '@angular/router';
import { Routes } from 'src/app/core/routes/routes';
import { PaginatorState } from 'primeng/paginator';
import { UserApi } from '@features/user/services/user-api';
import { BusinessApi } from '@features/business/services/business-api';
import { DataEndpoint } from '@core/models/general.model';
import { UserListModel, UserPaginatedModel, UserPaginatedRequestModel } from '@features/user/models/user.model';
import { BusinessGroupedModel } from '@features/business/models/business.model';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmationDialog } from "@shared/components/confirmation-dialog/confirmation-dialog";
import { BlockUI } from "primeng/blockui";
import { ProgressSpinner } from "primeng/progressspinner";
import { UserRole } from '@core/auth/services/auth-service';
import { NotHasRolePipe } from '@shared/pipes/not-has-role.pipe';

@Component({
  selector: 'app-user',
  imports: [
    ButtonModule,
    UsersList,
    UserFilter,
    ToastModule,
    ConfirmationDialog,
    BlockUI,
    ProgressSpinner,
    NotHasRolePipe
],
  templateUrl: './user.html',
  styleUrl: './user.scss',
  providers: [MessageService]
})
export class User {
  private router = inject(Router);
  private messageService = inject(MessageService);
  private businessApi = inject(BusinessApi);
  private userApi = inject(UserApi);

  data: UserListModel[] = [];
  filteredData: UserListModel[] = [];
  isLoading: boolean = false;
  sortBy: string | null = null;
  sortOrder: 'asc' | 'desc' | null = null;
  UserRole = UserRole;

  // Filtros
  currentFilters: UserFilterModel = {};
  businessGroupOptions: BusinessGroupedModel[] = [];

  // Estado de paginación
  paginationState = signal<PaginatorState>({
    first: 0,
    rows: 10,
    page: 0
  });
  totalRecords: number = 0;

  // Variables para gestionar activar/inactivar usuario
  showStatusChangeDialog: boolean = false;
  selectedUser: UserListModel | null = null;
  changeStatusMessage: string = '';
  changeStatusButton: string = '';

  ngOnInit() {
    this.loadBusinessGroups();
    this.loadData();
  }

  loadBusinessGroups() {
    this.businessApi.getBusinessGrouped().subscribe({
      next: (response: DataEndpoint<BusinessGroupedModel[]>) => {
        if (response.Status === 200) {
          this.businessGroupOptions = response.Data ?? [];
        }
      },
      error: (error: HttpErrorResponse) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message
        });
      }
    });
  }

  loadData() {
    this.isLoading = true;

    const request: UserPaginatedRequestModel = {
      PageNumber: (this.paginationState().page ?? 0) + 1,
      PageSize: this.paginationState().rows,
      SortBy: this.sortBy ? this.columnKeyMap[this.sortBy] : undefined,
      SortOrder: this.sortOrder ?? undefined,
      SearchTerm: this.currentFilters.searchText,
      BusinessGroup: this.currentFilters.selectedBusinessGroups
    }

    this.userApi.getUsersAll(request).subscribe({
      next: (response: DataEndpoint<UserPaginatedModel>) => {
        if (response.Status === 200) {
          this.data = response.Data?.records ?? [];
          this.filteredData = response.Data?.records ?? [];
          this.totalRecords = response.Data?.pagination.totalCount ?? 0;
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message });
      }
    })
  }

  onPageChange(state: PaginatorState) {
    this.paginationState.set(state);
    this.loadData();
  }

  handleConfirmChangeStatus(user: UserListModel) {
    // Guardar el usuario seleccionado para el cambio de estado
    this.selectedUser = user;

    const userName = user.Name + ' ' + user.LastName;
    // Si el usuario va a ser desactivado
    if (user.Status === 'ENABLED') {
      this.changeStatusMessage = `¿Está seguro de que desea desactivar al usuario ${userName}?`;
      this.changeStatusButton = 'Desactivar';
    }
    // Si el usuario va a ser activado
    else {
      this.changeStatusMessage = `¿Está seguro de que desea activar al usuario ${userName}?`;
      this.changeStatusButton = 'Activar';
    }

    this.showStatusChangeDialog = true;
  }

  confirmStatusChange() {
    if (this.selectedUser) {
      this.changeUserStatus(this.selectedUser.Id);
    }
    this.showStatusChangeDialog = false;
    this.selectedUser = null;
  }

  cancelStatusChange() {
    this.showStatusChangeDialog = false;
    this.selectedUser = null;
  }

  changeUserStatus(userId: string) {
    this.isLoading = true;

    this.userApi.updateUserStatus(userId).subscribe({
      next: (response: DataEndpoint<any>) => {
        if (response.Status === 200) {
          this.messageService.add({
            severity: 'success',
            summary: 'Acción exitosa',
            detail: 'Estado actualizado correctamente'
          });
          this.loadData();
        }
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error?.error.Message });
      }
    })
  }

  onCreateUser() {
    this.router.navigate([Routes.usersAddPage])
  }

  onEditUser(userId: string) {
    this.router.navigate([Routes.usersEditPage.replace(':id', userId)])
  }

  // Configurar las columnas con sort
  private readonly columnKeyMap: Record<string, string> = {
    Name: 'Name',
    LastName: 'LastName',
    Email: 'Email',
    Phone: 'Phone',
    Businesses: 'Businesses',
    BusinessGroupName: 'BusinessGroupName',
    Status: 'Status',
    CreatedAt: 'CreatedAt',
    UpdatedAt: 'UpdatedAt'
  };

  // Toggle de estados: null -> asc -> desc -> null
  toggleSort(columnKey: string) {
    if (!this.columnKeyMap[columnKey]) return;

    if (this.sortBy !== columnKey) {
      // Nueva columna -> iniciar asc
      this.sortBy = columnKey;
      this.sortOrder = 'asc';
    } else {
      // Misma columna -> avanzar el ciclo
      if (this.sortOrder === 'asc') this.sortOrder = 'desc';
      else if (this.sortOrder === 'desc') {
        // Quitar sort
        this.sortBy = null;
        this.sortOrder = null;
      } else {
        this.sortOrder = 'asc';
      }
    }

    // Resetear la paginación al aplicar un nuevo orden
    this.paginationState().page = 0;
    this.loadData();
  }

  handleFilterChange(filters: UserFilterModel) {
    this.currentFilters = filters;
    // Resetear paginación al filtrar
    this.paginationState.set({
      ...this.paginationState(),
      first: 0,
      page: 0
    });
    this.loadData();
  }

  handleClearFilters() {
    this.currentFilters = {};
    // Resetear paginación al limpiar filtros
    this.paginationState.set({
      ...this.paginationState(),
      first: 0,
      page: 0
    });
    this.loadData();
  }
}
